import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getRecipient,
  saveRecipientDraft,
  updateRecipient,
} from 'domains/Onboarding/Mexico/services';
import { IRecipient, RecipientState } from 'domains/Onboarding/Mexico/types';

const recipientInitialState: RecipientState = {
  getRecipient: {
    data: null,
    status: 'idle',
    error: undefined,
  },
  updateRecipient: {
    data: null,
    status: 'idle',
    error: undefined,
  },
};

const setRecipient = (
  state: RecipientState,
  action: PayloadAction<IRecipient>,
) => {
  state.getRecipient.data = action.payload;
  state.getRecipient.status = 'success';
};

export const getRecipientAction = createAsyncThunk(
  'recipient/getRecipientAction',
  async (storeId: string) => {
    return await getRecipient(storeId);
  },
);

export const updateRecipientAction = createAsyncThunk(
  'recipient/updateRecipientAction',
  async (payload: { storeId: string; recipient: IRecipient }) => {
    const response = await updateRecipient(payload.storeId, payload.recipient);
    return response;
  },
);

// Shares the updateRecipient state so loading, error and success handling
// work the same for both form buttons.
export const saveRecipientDraftAction = createAsyncThunk(
  'recipient/saveRecipientDraftAction',
  async (payload: { storeId: string; recipient: IRecipient }) => {
    return await saveRecipientDraft(payload.storeId, payload.recipient);
  },
);

const recipientSlice = createSlice({
  name: 'recipient',
  initialState: recipientInitialState,
  reducers: {
    setRecipientAction: setRecipient,
    resetRecipientAction(state) {
      state.getRecipient.status = 'idle';
      state.getRecipient.data = null;
      state.updateRecipient.status = 'idle';
      state.updateRecipient.data = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRecipientAction.pending, (state) => {
        state.getRecipient.status = 'loading';
      })
      .addCase(getRecipientAction.fulfilled, (state, action) => {
        state.getRecipient.status = 'success';
        state.getRecipient.data = action.payload;
      })
      .addCase(getRecipientAction.rejected, (state, action) => {
        state.getRecipient.status = 'error';
        state.getRecipient.error = action.error.message;
      })
      .addCase(updateRecipientAction.pending, (state) => {
        state.updateRecipient.status = 'loading';
      })
      .addCase(updateRecipientAction.fulfilled, (state, action) => {
        state.updateRecipient.status = 'success';
        state.updateRecipient.data = action.payload;
      })
      .addCase(updateRecipientAction.rejected, (state, action) => {
        state.updateRecipient.status = 'error';
        state.updateRecipient.error = action.error.message;
      })
      .addCase(saveRecipientDraftAction.pending, (state) => {
        state.updateRecipient.status = 'loading';
      })
      .addCase(saveRecipientDraftAction.fulfilled, (state, action) => {
        state.updateRecipient.status = 'success';
        state.updateRecipient.data = action.payload;
      })
      .addCase(saveRecipientDraftAction.rejected, (state, action) => {
        state.updateRecipient.status = 'error';
        state.updateRecipient.error = action.error.message;
      });
  },
});

export const { setRecipientAction, resetRecipientAction } =
  recipientSlice.actions;

export default recipientSlice.reducer;
